// @flow
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { F, propOr, sort } from "ramda";

import { MultiSelect } from "./MultiSelect";
import { SingleSelect } from "./SingleSelect";

import { type Id } from "../../model";

import { isSeletorAberto, setSeletorAberto } from "../../state";

const convertValue = (value: string) => {
  if (value.match(/^\d+$/)) {
    const intValue = parseInt(value, 10);
    return isNaN(intValue) ? value : intValue;
  } else {
    return value;
  }
};

type Props = {
  alwaysOpen?: boolean,
  focused?: boolean,
  className?: ?string,
  value: Id | ?Array<Id> | string,
  valueToScroll?: string | null,
  options: Array<any>,
  campoId: string,
  campoDescricao: string,
  multiple?: boolean,
  searchable?: boolean,
  size?: number,
  defaultOption?: string,
  onChange: any => any,
  disabled: boolean,
  isOptionDisabled?: any => boolean,
  selectedOptionsFirst?: boolean,
  sorting?: "alphabetical",
  open?: boolean,
  onOpen?: () => void,
  onClose?: () => void,
};

function SelectInput({
  alwaysOpen = false,
  focused = true,
  className,
  value = "",
  valueToScroll,
  options = [],
  campoId,
  campoDescricao,
  multiple = false,
  searchable = true,
  size = 1,
  defaultOption,
  onChange,
  disabled,
  isOptionDisabled = F,
  sorting,
  /* Essa opção por enquanto só possui efeito para MultiSelect */
  selectedOptionsFirst = false,
  /* Essas opções por enquanto só possuem efeito para SingleSelect */
  open,
  onOpen,
  onClose,
  ...props
}: Props) {
  const sortedOptions = useMemo(() => {
    switch (sorting) {
      case "alphabetical":
        return sort((optionA, optionB) =>
          propOr("", "value", optionA).localeCompare(
            propOr("", "value", optionB)
          )
        )(options);

      default:
        return options;
    }
  }, [options, sorting]);

  // Por motivos desconhecidos, ao usar useMemo para armazenar as opções convertidas,
  // o Select passa a não permitir marcar/desmarcar a mesma opção de forma consecutiva.
  // TODO: Investigar mais a fundo o problema, permitindo restaurar o uso de useMemo aqui.
  const convertedOptions = sortedOptions.map(option => ({
    id: String(option[campoId]),
    value: option[campoDescricao],
  }));

  return multiple ? (
    <MultiSelect
      focused={focused}
      className={className}
      options={convertedOptions}
      value={value != null && Array.isArray(value) ? value.map(String) : []}
      valueToScroll={valueToScroll}
      disabled={disabled}
      isOptionDisabled={isOptionDisabled}
      selectedOptionsFirst={selectedOptionsFirst}
      onChange={values => onChange(values.map(convertValue))}
    />
  ) : (
    <SingleSelect
      {...props}
      alwaysOpen={alwaysOpen}
      focused={focused}
      className={className}
      defaultOption={defaultOption}
      options={convertedOptions}
      value={value != null ? String(value) : ""}
      disabled={disabled}
      isOptionDisabled={isOptionDisabled}
      onChange={value => onChange(convertValue(value))}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      searchable={searchable}
    />
  );
}

SelectInput.defaultProps = {
  alwaysOpen: false,
  campoId: "id",
  campoDescricao: "chaveTraducao",
  disabled: false,
};

export const useSelectState = (nome: string) => {
  const dispatch = useDispatch();

  const open = useSelector(isSeletorAberto(nome));

  const onOpen = useCallback(() => dispatch(setSeletorAberto(nome, true)), [
    dispatch,
    nome,
  ]);
  const onClose = useCallback(() => dispatch(setSeletorAberto(nome, false)), [
    dispatch,
    nome,
  ]);

  return { onOpen, onClose, open };
};

export default SelectInput;
